import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { PublicHeader } from '../layout/PublicHeader';
import { useAuth } from '../../hooks/useAuth';

// Chargement dynamique des templates d'en-tête
const headerModules = import.meta.glob('/src/templates/headers/**/*.{tsx,jsx,ts,js}');

export const HeaderSlot: React.FC = () => {
  const { tenant } = useTenantContext();
  const { isAuthenticated, checkAuth } = useAuth();

  const selectedPath = useMemo(() => {
    const templates = (tenant?.config?.templates || {}) as any;
    const headers = (templates.headers || {}) as Record<string, string>;
    return headers.default || '';
  }, [tenant]);

  const [HeaderComponent, setHeaderComponent] = useState<React.ComponentType | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [tenantLoading, setTenantLoading] = useState(!tenant);
  const [adminShortcutCollapsed, setAdminShortcutCollapsed] = useState(true);

  const isAdmin = useMemo(() => {
    return isAuthenticated;
  }, [isAuthenticated]);

  // Suivre le chargement du tenant
  useEffect(() => {
    setTenantLoading(!tenant);
  }, [tenant]);

  // Charger l'état de rétraction depuis localStorage
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('sis_admin_shortcut_collapsed');
      if (stored === 'true') {
        setAdminShortcutCollapsed(true);
      }
    } catch (err) {
      console.error('Erreur lors du chargement de la préférence de raccourci admin:', err);
    }
  }, []);

  // Sauvegarder l'état de rétraction
  useEffect(() => {
    try {
      window.localStorage.setItem('sis_admin_shortcut_collapsed', adminShortcutCollapsed ? 'true' : 'false');
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la préférence de raccourci admin:', err);
    }
  }, [adminShortcutCollapsed]);

  // Vérifier l'authentification au montage pour récupérer une éventuelle session existante
  useEffect(() => {
     checkAuth().catch((err) => {
       console.error('Erreur lors de la vérification de la session admin dans HeaderSlot:', err);
     });
   }, [checkAuth]);

  useEffect(() => {
    let mounted = true;
    if (selectedPath && headerModules[selectedPath]) {
      setLoadingTemplate(true);
      const loader = headerModules[selectedPath] as () => Promise<{ default: React.ComponentType }>;
      loader()
        .then((mod) => {
          if (mounted) setHeaderComponent(() => mod.default);
        })
        .catch((err) => {
          console.error('Erreur de chargement du header template:', err);
          if (mounted) setHeaderComponent(null);
        })
        .finally(() => mounted && setLoadingTemplate(false));
    } else {
      setHeaderComponent(null);
      setLoadingTemplate(false);
    }
    return () => {
      mounted = false;
    };
  }, [selectedPath]);

  const adminShortcut = isAdmin ? (
    <div className="fixed top-2 right-2 z-[9999] flex items-center gap-2">
      {adminShortcutCollapsed ? (
        <button
          type="button"
          onClick={() => setAdminShortcutCollapsed(false)}
          className="inline-flex items-center justify-center rounded-full h-9 w-9 text-xs font-semibold shadow-lg bg-black/80 text-white hover:bg-black/90 border border-white/20 backdrop-blur-sm"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 mr-1" />
          A
        </button>
      ) : (
        <div className="flex items-center gap-1">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold shadow-lg bg-black/80 text-white hover:bg-black/90 border border-white/20 backdrop-blur-sm"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Espace admin</span>
          </Link>
          <button
            type="button"
            onClick={() => setAdminShortcutCollapsed(true)}
            className="inline-flex items-center justify-center rounded-full h-7 w-7 text-[10px] font-semibold shadow-lg bg-black/70 text-white hover:bg-black/90 border border-white/20 backdrop-blur-sm"
            aria-label="Masquer le raccourci admin"
          >
            ×
          </button>
        </div>
      )}
    </div>
  ) : null;

  if (tenantLoading || loadingTemplate) return null;
  if (HeaderComponent) {
    return (
      <>
        <HeaderComponent />
        {adminShortcut}
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      {adminShortcut}
    </>
  );
};
